const WORLD_BANK_COUNTRIES_URL = 'https://api.worldbank.org/v2/country';

export type PurchasingPowerHint = {
    label: string;
    detail: string;
    color: 'default' | 'warning' | 'info' | 'success';
    pricingMultiplier: number;
};

type WorldBankCountry = {
    iso2Code: string;
    incomeLevel: {
        id: string;
        value: string;
    };
};

let countryIncomeLevelsPromise: Promise<Map<string, string>> | null = null;

function isWorldBankCountry(value: unknown): value is WorldBankCountry {
    if (!value || typeof value !== 'object') return false;

    const record = value as Record<string, unknown>;
    const incomeLevel = record.incomeLevel;
    return (
        typeof record.iso2Code === 'string' &&
        Boolean(incomeLevel) &&
        typeof incomeLevel === 'object' &&
        typeof (incomeLevel as Record<string, unknown>).id === 'string'
    );
}

async function fetchCountryIncomeLevels(): Promise<Map<string, string>> {
    const url = new URL(WORLD_BANK_COUNTRIES_URL);
    url.searchParams.set('format', 'json');
    url.searchParams.set('per_page', '400');

    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`World Bank returned HTTP ${response.status}`);
    }

    const payload: unknown = await response.json();
    if (!Array.isArray(payload) || !Array.isArray(payload[1])) {
        throw new Error('World Bank returned an unexpected country response');
    }

    const incomeLevels = new Map<string, string>();
    payload[1].filter(isWorldBankCountry).forEach((country) => {
        const countryCode = country.iso2Code.trim().toUpperCase();
        const incomeLevel = country.incomeLevel.id.trim().toUpperCase();
        if (countryCode && incomeLevel) {
            incomeLevels.set(countryCode, incomeLevel);
        }
    });

    return incomeLevels;
}

function getCountryIncomeLevels(): Promise<Map<string, string>> {
    if (!countryIncomeLevelsPromise) {
        countryIncomeLevelsPromise = fetchCountryIncomeLevels().catch((error: unknown) => {
            countryIncomeLevelsPromise = null;
            throw error;
        });
    }

    return countryIncomeLevelsPromise;
}

const premiumMarketCountryCodes = new Set(['US', 'CH', 'NO', 'SG', 'LU', 'DK']);
const matureMarketCountryCodes = new Set([
    'AT', 'BE', 'CA', 'DE', 'FI', 'FR', 'GB', 'IE', 'IS', 'JP', 'KR', 'NL', 'NZ', 'SE',
]);

function hintForCountry(countryCode: string, incomeLevel: string | undefined): PurchasingPowerHint {
    if (premiumMarketCountryCodes.has(countryCode)) {
        return {
            label: 'Premium Market',
            detail: 'Top commercial pricing tier. The United States benchmark is weighted at 1.80x the India value-market benchmark.',
            color: 'success',
            pricingMultiplier: 1.8,
        };
    }

    if (matureMarketCountryCodes.has(countryCode)) {
        return {
            label: 'Mature Market',
            detail: 'High-income mature market priced at 1.50x the India value-market benchmark.',
            color: 'success',
            pricingMultiplier: 1.5,
        };
    }

    switch (incomeLevel) {
        case 'LIC':
            return {
                label: 'Access Market',
                detail: 'Access-focused market priced at 0.85x the India value-market benchmark.',
                color: 'warning',
                pricingMultiplier: 0.85,
            };
        case 'LMC':
            return {
                label: 'Value Market',
                detail: 'Value-market benchmark. India is the reference market at 1.00x.',
                color: 'warning',
                pricingMultiplier: 1,
            };
        case 'UMC':
            return {
                label: 'Growth Market',
                detail: 'Growth market priced at 1.15x the India value-market benchmark.',
                color: 'info',
                pricingMultiplier: 1.15,
            };
        case 'HIC':
            return {
                label: 'Established Market',
                detail: 'Established high-income market priced at 1.30x the India value-market benchmark. Australia uses this tier.',
                color: 'success',
                pricingMultiplier: 1.3,
            };
        default:
            return {
                label: 'Standard Market',
                detail: 'No classification was available, so the neutral 1.00x benchmark is used.',
                color: 'default',
                pricingMultiplier: 1,
            };
    }
}

export async function getPurchasingPowerHints(
    countryCodes: string[],
): Promise<Record<string, PurchasingPowerHint>> {
    const incomeLevels = await getCountryIncomeLevels();

    return Object.fromEntries(
        countryCodes.map((countryCode) => {
            const normalizedCode = countryCode.trim().toUpperCase();
            return [normalizedCode, hintForCountry(normalizedCode, incomeLevels.get(normalizedCode))];
        }),
    );
}
